import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Navigate } from 'react-router-dom';
import Loader from '../../components/Loader';
import useCurrentUser from '../../hooks/useCurrentUser';
import { createAccount, deleteAccount, fetchAccounts, updateAccount } from '../../services/userService';

const PageContainer = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  margin: 0;
`;

const PrimaryButton = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  &:hover {
    background: #0056b3;
  }
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
`;

const SmallButton = styled.button`
  background: ${(props) => (props.danger ? '#dc3545' : props.secondary ? '#6c757d' : '#007bff')};
  color: white;
  border: none;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  margin-right: 5px;
  &:hover {
    opacity: 0.85;
  }
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
`;

const Th = styled.th`
  background: #002d62;
  color: white;
  padding: 10px;
  text-align: left;
  font-size: 13px;
`;

const Td = styled.td`
  padding: 8px 10px;
  border-bottom: 1px solid #eee;
  font-size: 13px;
  vertical-align: middle;
`;

const Chip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  margin: 1px 4px 1px 0;
  border-radius: 10px;
  font-size: 11px;
  background: #e9ecef;
  color: #333;
`;

const Message = styled.div`
  padding: 10px;
  border-radius: 5px;
  margin-bottom: 15px;
  background: ${(props) => (props.error ? '#f8d7da' : '#d4edda')};
  color: ${(props) => (props.error ? '#721c24' : '#155724')};
`;

const ModalBackground = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 5px;
  width: 480px;
  max-width: 92%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-weight: bold;
  margin-bottom: 4px;
  font-size: 13px;
`;

const Hint = styled.div`
  font-size: 11px;
  color: #666;
  margin-top: 3px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 5px;
  box-sizing: border-box;
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const AccountModal = ({ mode, initial, onClose, onSave }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      // ftpUsername is intentionally not sent: the field is hidden for now and
      // omitting it preserves whatever the account already has
      await onSave({
        name: form.name.trim(),
        customerNumbers: form.customerNumbers
          .split(',')
          .map((n) => n.trim())
          .filter(Boolean),
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalBackground>
      <ModalContent>
        <h3 style={{ marginTop: 0 }}>{mode === 'create' ? 'New account' : `Edit ${initial.name}`}</h3>
        {error && <Message error>{error}</Message>}
        <form onSubmit={submit}>
          <FormGroup>
            <Label>Account name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
          </FormGroup>
          <FormGroup>
            <Label>Customer numbers</Label>
            <Input
              value={form.customerNumbers}
              onChange={(e) => setForm({ ...form, customerNumbers: e.target.value })}
              placeholder="e.g. 10, 20, 30"
            />
            <Hint>
              Two-digit numbers this account owns, comma separated. They route every camera and location (MD 10101 → 10) and
              can never be shared between companies.
            </Hint>
          </FormGroup>
          <ModalButtons>
            <SmallButton type="button" secondary onClick={onClose} disabled={saving}>
              Cancel
            </SmallButton>
            <PrimaryButton type="submit" disabled={saving}>
              {saving ? 'Saving…' : mode === 'create' ? 'Create account' : 'Save changes'}
            </PrimaryButton>
          </ModalButtons>
        </form>
      </ModalContent>
    </ModalBackground>
  );
};

const emptyForm = { name: '', customerNumbers: '' };
const toForm = (a) => ({ name: a.name, customerNumbers: (a.customerNumbers || []).join(', ') });

const AccountsPage = () => {
  const { user: me, loading: meLoading, isAdmin } = useCurrentUser();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setAccounts(await fetchAccounts());
      setMessage(null);
    } catch (err) {
      setMessage({ error: true, text: `Could not load accounts: ${err.message}` });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const handleSave = async (values) => {
    if (modal.mode === 'create') {
      await createAccount(values);
      setMessage({ error: false, text: `Created ${values.name}.` });
    } else {
      await updateAccount(modal.account.id, values);
      setMessage({ error: false, text: `Saved ${values.name}.` });
    }
    await load();
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete the account "${a.name}"?`)) return;
    try {
      await deleteAccount(a.id);
      setMessage({ error: false, text: `Deleted ${a.name}.` });
      await load();
    } catch (err) {
      setMessage({ error: true, text: err.message });
    }
  };

  if (meLoading) return <Loader />;
  if (!isAdmin) return <Navigate to="/insights" replace />;

  return (
    <PageContainer>
      <Header>
        <Title>Accounts</Title>
        <PrimaryButton onClick={() => setModal({ mode: 'create' })}>New account</PrimaryButton>
      </Header>
      {message && <Message error={message.error}>{message.text}</Message>}
      {loading ? (
        <Loader />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>ACCOUNT</Th>
              <Th>CUSTOMER #s</Th>
              <Th>FTP USER</Th>
              <Th>CUSTOMERS</Th>
              <Th>USERS</Th>
              <Th>ACTIONS</Th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.id}>
                <Td>
                  <strong>{a.name}</strong>
                </Td>
                <Td>{(a.customerNumbers || []).map((n) => <Chip key={n}>{n}</Chip>)}</Td>
                <Td>{a.ftpUsername || '—'}</Td>
                <Td>{a.customerCount}</Td>
                <Td>{a.userCount}</Td>
                <Td>
                  <SmallButton onClick={() => setModal({ mode: 'edit', account: a })}>Edit</SmallButton>
                  <SmallButton
                    danger
                    onClick={() => handleDelete(a)}
                    disabled={!!(a.customerCount || a.userCount)}
                    title={a.customerCount || a.userCount ? 'Reassign its customers and users first' : undefined}
                  >
                    Delete
                  </SmallButton>
                </Td>
              </tr>
            ))}
            {!accounts.length && (
              <tr>
                <Td colSpan={6}>No accounts yet — create the first one.</Td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
      {modal && (
        <AccountModal
          mode={modal.mode}
          initial={modal.mode === 'create' ? emptyForm : toForm(modal.account)}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}
    </PageContainer>
  );
};

export default AccountsPage;
